/**
 * Hestan Vineyards variant — UI overrides + extras for tenant `hestanvineyards`.
 *
 * Hestan forked the same original as Halleck/Goose. It uses:
 *   - Halleck's WineCard (identical: full-width add-to-cart, bottle_quantity,
 *     top SOLD OUT overlay) + the shared Membership/Event/EventDetails/Review
 *     components.
 *   - Goose-style contact: a "Contact our team" guided button opens the
 *     Text/Email/Call menu (NOT Halleck's direct Text-a-Live-Person).
 *
 * GUIDED FLOW (old hestan_vineyards/script.js 1387-1402):
 *   1. Taste our wines        → "Show me all tasting experiences"
 *   2. Shop our wines         → "Show me all available wines"
 *   3. Explore our wine club  → "Show me all wine club memberships"
 *   4. Events                 → "Show me all the upcoming events" (is_event=false
 *                                by default, so hidden unless admin enables it)
 *   5. Reviews                → "Show me all the reviews" (BE-driven createReviews)
 *   6. Contact our team       → __contact__ (Email Us + Call Us ONLY — Hestan's
 *      old showContactOptions had no "Text a Live Person", unlike goose)
 *
 * Reference: hestan_vineyards/script.js Lines 1387-1402 (buttons),
 *   2860-2889 (showContactOptions → Email Us / Call Us only).
 */

import { on, addMessage, getState, setState } from "../../core/state.js";
import { getContact } from "../../core/configAccessors.js";
import { registerMessageKind } from "../../components/base/messages/index.js";
import { registry } from "../../core/registry.js";
import ContactOptions from "../../components/base/messages/ContactOptions.js";
import HestanWineCard from "./components/WineCard.js";
import HestanEventCard from "./components/EventCard.js";
import HestanEventDetailsCard from "./components/EventDetailsCard.js";
import HestanMembershipCard from "./components/MembershipCard.js";

const GUIDED_FLOW = [
  { label: "Taste our wines",        value: "Show me all tasting experiences",   icon: "bolt" },
  { label: "Shop our wines",         value: "Show me all available wines",       icon: "bolt" },
  { label: "Explore our wine club",  value: "Show me all wine club memberships", icon: "bolt" },
  { label: "Events",                 value: "Show me all the upcoming events",   icon: "bolt", visible_when: { is_event: true } },
  { label: "Reviews",                value: "Show me all the reviews",           icon: "bolt" },
  { label: "Contact our team",       value: "__contact__",                       icon: "bolt" },
];

// QR entries hide the tasting chip unless openagent forces it. Applied over
// whichever chip list is active (admin config or the built-in fallback).
function applyQrFilter(chips) {
  const { isQR, openagent } = getState().session || {};
  const showTasting = !isQR || openagent;
  return chips.filter(
    (item) => showTasting || item.value !== "Show me all tasting experiences"
  );
}

export function getGuidedFlow() {
  return applyQrFilter(GUIDED_FLOW);
}

function postDefaultOptions() {
  addMessage({ role: "bot", kind: "text", data: { text: "Is there anything else I can help you with?" } });
  addMessage({ role: "bot", kind: "prompts", data: { items: getGuidedFlow() } });
}

function telHref(phone) {
  return `tel:${String(phone || "").replace(/[^+0-9]/g, "")}`;
}

export async function register({ config, shadow }) {
  registerMessageKind("contact-options", ContactOptions);

  registry.registerComponent("WineCard", HestanWineCard);
  registry.registerComponent("EventCard", HestanEventCard);
  registry.registerComponent("EventDetailsCard", HestanEventDetailsCard);
  registry.registerComponent("MembershipCard", HestanMembershipCard);
  // Reviews are BE-driven (reviews_information → "review" card kind), rendered
  // per-item by this override with platform icons. Parity with old createReviews
  // (renders the BE feature_cards), NOT a hardcoded client-side button row.

  // Welcome guided buttons come from tenant config (guided_flow_questions),
  // edited in admin; fall back to the built-in GUIDED_FLOW. Apply the per-session
  // QR filter over whichever list is active.
  // Keep promo strip undimmed so promotional_banner.png + white CLICK HERE pill
  // match old .promo-text-right (ss1), not Fogarty's dimmed transparent CTA (ss2).
  const cfg = getState().tenant || {};
  const configured =
    Array.isArray(cfg.guided_flow_questions) && cfg.guided_flow_questions.length
      ? cfg.guided_flow_questions
      : GUIDED_FLOW;
  const theme = {
    ...(cfg.theme || {}),
    banner_promo_dim: false,
  };
  setState({
    tenant: {
      ...cfg,
      theme,
      guided_flow_questions: applyQrFilter(configured),
    },
  });

  on("ws-prompt-select", ({ value }) => {
    // Contact our team → Email Us / Call Us menu (Hestan's old showContactOptions
    // had no "Text a Live Person", unlike goose). Reviews are BE-driven (the chip
    // sends "Show me all the reviews"); the BE returns reviews_information rendered
    // by the HestanReviewCard override.
    if (value === "__contact__") {
      addMessage({ role: "bot", kind: "text", data: { text: "How would you like to connect with our team?" } });
      addMessage({
        role: "bot",
        kind: "contact-options",
        data: { email: getContact("email"), phone: getContact("phone"), text_live_person: false },
      });
    }
  });

  on("ws-contact-call", () => {
    const phone = getContact("phone");
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: `You can call us at [${phone}](${telHref(phone)}). Our team is available during business hours.` },
    });
    postDefaultOptions();
  });

  on("ws-contact-email", () => {
    const phone = getContact("phone");
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: `We'll get back to you within 24–48 hours. For faster assistance, please call us at [${phone}](${telHref(phone)}). Our team is available during business hours.` },
    });
    postDefaultOptions();
  });
}
